import { orderStatusText, panelOutcome, panelStage, t, type MessageKey } from '@aquabox/shared';
import type { OrderChannel, OrderStatus, PaymentType } from '../api/types';

/** Kullanıcıya görünen tüm metinler i18n'den gelir; burada yalnız anahtar eşlemesi var. */
export const statusLabel = (s: OrderStatus): string => t(`orderStatus.${s}` as MessageKey);
/**
 * `Order.status` etiketi. `HANDLED_EXTERNALLY` + ham durum varsa panelOutcome'a göre
 * "Panelde teslim edildi / iptal / işlemde" yazılır — siparişin paneldeki gerçek aşaması
 * eski tek "Panelde kapandı" etiketiyle kayboluyordu. Detay: `panel-stage.ts`.
 */
export const statusText = (
  status: OrderStatus,
  externalStatusRaw: string | null | undefined,
): string => orderStatusText(status, externalStatusRaw);

export const channelLabel = (c: OrderChannel): string => t(`orderChannel.${c}` as MessageKey);
export const paymentLabel = (p: PaymentType): string => t(`paymentType.${p}` as MessageKey);

/**
 * `HANDLED_EXTERNALLY` durum rengi: outcome'a göre (`done` yeşil, `cancelled` kırmızı,
 * `pending` kehribar). Diğer durumlar `STATUS_COLOR`'dan okunur. Renk istemi yalnız etiket
 * için, marka ayrımı (Hayat/Abant) gereksiz — aynı durum her iki panelde aynı renge boyanır.
 */
export function statusColor(
  status: OrderStatus,
  externalStatusRaw: string | null | undefined,
): string {
  if (status === 'HANDLED_EXTERNALLY') {
    const outcome = externalStatusRaw ? panelOutcome(externalStatusRaw) : null;
    if (outcome === 'delivered') return 'var(--success)';
    if (outcome === 'cancelled') return 'var(--danger)';
    if (outcome === 'pending') return 'var(--warning)';
    return STATUS_COLOR.HANDLED_EXTERNALLY;
  }
  return STATUS_COLOR[status];
}

export { panelStage };

/** Durum renkleri (§1.1). */
export const STATUS_COLOR: Record<OrderStatus, string> = {
  NEW: 'var(--info)',
  CONFIRMED: 'var(--primary)',
  ASSIGNED: 'var(--purple)',
  ON_ROUTE: 'var(--warning)',
  DELIVERED: 'var(--success)',
  LEFT_AT_DOOR: 'var(--warning)',
  FAILED: 'var(--danger)',
  CANCELLED: 'var(--danger)',
  HANDLED_EXTERNALLY: 'var(--text-secondary)',
};

/**
 * Kanal renkleri (§1.1).
 *
 * KAYNAĞA DEĞİL KANALA bağlı (ENTEGRASYON.md §3): Hayat panelinden çekilen bir Trendyol
 * siparişi turuncu Trendyol rozetiyle görünmeli, Hayat mavisiyle değil.
 */
export const CHANNEL_COLOR: Record<OrderChannel, string> = {
  GETIR_SU: 'var(--ch-getir)',
  TRENDYOL: 'var(--ch-trendyol)',
  YEMEKSEPETI: 'var(--ch-yemeksepeti)',
  PHONE: 'var(--ch-phone)',
  WHATSAPP: 'var(--success)',
  BRAND_APP: 'var(--ch-hayat)',
  UNKNOWN: 'var(--text-secondary)',
};

/** Kanal harf kutusu: kalem adında "abant" geçiyorsa A, aksi hâlde H (§1.6). */
export function brandLetter(productName: string): 'H' | 'A' {
  return productName.toLocaleLowerCase('tr-TR').includes('abant') ? 'A' : 'H';
}

/** Sipariş kalemlerinin özeti: "2× Hayat 19L · 1× 0,5L Koli". */
export function itemsSummary(items: { qty: number; product: { name: string } }[]): string {
  return items.map((i) => `${i.qty}× ${i.product.name}`).join(' · ');
}

/** "25 dk önce" / "şimdi" / "2 sa önce" / "3 gün önce". */
export function relativeTime(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return t('time.now');
  if (minutes < 60) return t('time.minutesAgo', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('time.hoursAgo', { n: hours });
  return t('time.daysAgo', { n: Math.floor(hours / 24) });
}

// Cari bakiye gösterimi burada DEĞİL: tek yerda `components/BalanceCell.tsx` (B/A eki,
// yön ham string'in '-' işaretinden okunur). `balanceDisplay` kaldırıldı — hiçbir ekran
// kullanmıyordu ve eski "−₺240,00" dili müşteri ekstresiyle ("₺240,00 B") çelişiyordu.
